import { z } from 'zod';

import { ConnectorsDetailsMode } from '../../scenes/Connector/utils';

/**
 * Be aware that LaunchDarkly feature flags will be lowerCamelCased, regardless of how they are named.
 * For example: `FlowBuilderWorkTitle` will be `flowBuilderWorkTitle` and `flag-name` will be `flagName`.
 */
export enum FeatureFlagKey {
  testCanary = 'testCanary',
  blockConfigErrorsInDrawer = 'blockConfigErrorsInDrawer',
  flowCostEstimate = 'flowCostEstimate',
  runScenarioTestOptions = 'runScenarioTestOptions',
  canCreateCustomAnalyticEvents = 'canCreateCustomAnalyticEvents',
  enableV2Payments = 'enableV2Payments',
  canConfigureSignalV2 = 'canConfigureSignalV2',
  canConfigurePreAssistantMessage = 'canConfigurePreAssistantMessage',
  canConfigureAutoRedirectFromSchedulingPrimitive = 'canConfigureAutoRedirectFromSchedulingPrimitive',
  autoCompleteX12 = 'autoCompleteX12',
  testRunProductionPatientOptions = 'testRunProductionPatientOptions',
  deprecateAgentPages = 'deprecateAgentPages',
  enablePntDataSubmission = 'enablePntDataSubmission',
  appointmentVoiceReminders = 'appointmentVoiceReminders',
  enableSidekickAutomation = 'enableSidekickAutomation',
  omniContentEmbedMode = 'omniContentEmbedMode',
  connectorsOptions = 'connectorOptions',
  flowInputAutoTypeOptions = 'flowInputAutoTypeOptions',
  triggerOptions = 'triggerOptions',
  feedValidationSettings = 'feedValidationSettings',
  advancedFhirBlockV2 = 'advancedFhirBlockV2',
  documentTemplatePreview = 'documentTemplatePreview',
  sidekickAiAutofill = 'sidekickAiAutofill',
  documentPacketGeneratorTool = 'documentPacketGeneratorTool',
  enableSidekickClinicalPacketTab = 'enableSidekickClinicalPacketTab',
  enableCustomToolsOnAssistants = 'enableCustomToolsOnAssistants',
  enableVoiceAssistantHandoffTab = 'enableVoiceAssistantHandoffTab',
  scheduledFlowTriggers = 'scheduledFlowTriggers',
  flowRunsActions = 'flowRunsActions',
  paymentDashboardV2 = 'paymentDashboardV2',
  enableAssignmentGroupsInsteadOfTaskCategories = 'enableAssignmentGroupsInsteadOfTaskCategories',
  ocrVlmBlockUi = 'ocrVlmBlockUi',
  scenarioTestV2 = 'scenarioTestV2',
  conversationTaskAssignmentGroup = 'conversationTaskAssignmentGroup',
  transformFileBlockFeatureVisibilityOptions = 'transformFileBlockFeatureVisibilityOptions',
}

export const FeatureFlagSchemas = {
  [FeatureFlagKey.testCanary]: z.string().default('None'),
  [FeatureFlagKey.blockConfigErrorsInDrawer]: z.boolean().default(false),
  [FeatureFlagKey.flowCostEstimate]: z.boolean().default(false),
  [FeatureFlagKey.runScenarioTestOptions]: z
    .object({
      buttonIsVisible: z.boolean(),
    })
    .default({
      buttonIsVisible: false,
    }),
  [FeatureFlagKey.canCreateCustomAnalyticEvents]: z.boolean().default(false),
  [FeatureFlagKey.enableV2Payments]: z.boolean().default(false),
  [FeatureFlagKey.canConfigureSignalV2]: z.boolean().default(false),
  [FeatureFlagKey.canConfigurePreAssistantMessage]: z.boolean().default(false),
  [FeatureFlagKey.canConfigureAutoRedirectFromSchedulingPrimitive]: z.boolean().default(false),
  [FeatureFlagKey.autoCompleteX12]: z
    .object({
      isEnabled: z.boolean().default(false),
    })
    .default({
      isEnabled: false,
    }),
  [FeatureFlagKey.testRunProductionPatientOptions]: z
    .object({
      canTestWithProductionPatient: z.boolean().default(false),
    })
    .default({
      canTestWithProductionPatient: false,
    }),
  [FeatureFlagKey.enablePntDataSubmission]: z
    .object({
      enablePntDataSubmission: z.boolean(),
    })
    .default({
      enablePntDataSubmission: false,
    }),
  [FeatureFlagKey.deprecateAgentPages]: z
    .object({
      flowsPageMode: z.enum(['LegacyAgentsPage', 'NewFlowsPage']).default('LegacyAgentsPage'),
    })
    .default({
      flowsPageMode: 'LegacyAgentsPage',
    }),
  [FeatureFlagKey.appointmentVoiceReminders]: z
    .object({
      isEnabled: z.boolean(),
    })
    .default({
      isEnabled: false,
    }),
  [FeatureFlagKey.enableSidekickAutomation]: z.boolean().default(false),
  [FeatureFlagKey.omniContentEmbedMode]: z
    .object({
      isEnabled: z.boolean(),
    })
    .default({
      isEnabled: false,
    }),
  [FeatureFlagKey.connectorsOptions]: z
    .object({
      isListScreenEnabled: z.boolean(),
      detailsMode: z.nativeEnum(ConnectorsDetailsMode),
      isEventsTabVisible: z.boolean(),
      isCreateEnabled: z.boolean(),
    })
    .default({
      isListScreenEnabled: false,
      detailsMode: ConnectorsDetailsMode.Drawer,
      isEventsTabVisible: false,
      isCreateEnabled: false,
    }),
  [FeatureFlagKey.flowInputAutoTypeOptions]: z
    .object({
      isEnabled: z.literal(true),
    })
    .or(
      z.object({
        isEnabled: z.literal(false),
      })
    )
    .default({
      isEnabled: false,
    }),
  [FeatureFlagKey.triggerOptions]: z
    .object({
      isEnabled: z.boolean(),
    })
    .default({
      isEnabled: false,
    }),
  [FeatureFlagKey.feedValidationSettings]: z.boolean().default(false),
  [FeatureFlagKey.advancedFhirBlockV2]: z
    .object({
      isSearchEndpointEnabled: z.boolean(),
    })
    .default({
      isSearchEndpointEnabled: false,
    }),
  [FeatureFlagKey.documentTemplatePreview]: z
    .object({
      useV2: z.boolean(),
    })
    .default({
      useV2: false,
    }),
  [FeatureFlagKey.sidekickAiAutofill]: z
    .object({
      isEnabled: z.boolean(),
    })
    .default({
      isEnabled: false,
    }),
  [FeatureFlagKey.documentPacketGeneratorTool]: z
    .object({
      isSubmitPriorAuthorizationEnabled: z.boolean(),
      isCustomSubtaskEnabled: z.boolean(),
    })
    .default({
      isSubmitPriorAuthorizationEnabled: false,
      isCustomSubtaskEnabled: false,
    }),
  [FeatureFlagKey.enableSidekickClinicalPacketTab]: z
    .object({
      isEnabled: z.literal(true),
    })
    .or(
      z.object({
        isEnabled: z.literal(false),
      })
    )
    .default({
      isEnabled: false,
    }),
  [FeatureFlagKey.enableCustomToolsOnAssistants]: z
    .object({ enabled: z.boolean() })
    .default({ enabled: false }),
  [FeatureFlagKey.enableVoiceAssistantHandoffTab]: z
    .object({ enabled: z.boolean() })
    .default({ enabled: false }),
  [FeatureFlagKey.scheduledFlowTriggers]: z.boolean().default(false),
  [FeatureFlagKey.flowRunsActions]: z
    .object({
      isBulkCancelByDialogEnabled: z.boolean(),
      isBulkCancelByRowEnabled: z.boolean(),
    })
    .default({
      isBulkCancelByDialogEnabled: false,
      isBulkCancelByRowEnabled: false,
    }),
  [FeatureFlagKey.paymentDashboardV2]: z.boolean().default(false),
  [FeatureFlagKey.enableAssignmentGroupsInsteadOfTaskCategories]: z
    .object({
      isEnabled: z.boolean(),
    })
    .default({ isEnabled: false }),
  [FeatureFlagKey.ocrVlmBlockUi]: z
    .object({
      isEnabled: z.boolean(),
    })
    .default({
      isEnabled: false,
    }),
  [FeatureFlagKey.scenarioTestV2]: z
    .object({
      isEnabled: z.boolean(),
    })
    .default({
      isEnabled: false,
    }),
  [FeatureFlagKey.conversationTaskAssignmentGroup]: z
    .object({
      isEnabledOnBlock: z.boolean(),
      isEnabledInListPage: z.boolean(),
    })
    .default({ isEnabledOnBlock: true, isEnabledInListPage: true }),
  [FeatureFlagKey.transformFileBlockFeatureVisibilityOptions]: z
    .object({
      splitPdfOperationVisibility: z.boolean(),
      combinePdfsOperationVisibility: z.boolean(),
    })
    .default({
      splitPdfOperationVisibility: false,
      combinePdfsOperationVisibility: false,
    }),
} as const;
